import { useCallback, useEffect, useState } from "react";
import type { CustomerBasketAdapter } from "../data/customer-basket-adapter";
import type { CustomerDataAdapter } from "../data/customer-adapter";
import {
  addressPickerFrom,
  isBookThePicker,
  type AddressPicker,
} from "./customer-address-book";

export type AddressPickerState =
  | { status: "loading" }
  | { status: "ready"; picker: AddressPicker }
  | { status: "error"; message: string };

/**
 * The delivery picker's addresses: the person's own address book once the server says it is the rule, otherwise
 * the customer's own list as before (docs/features/customer-portal-ordering/21). The customer's list is read only
 * while the book is not the rule: the real route is closed to a signed-in person once it is.
 *
 * A reload keeps the last answer on screen while it reads again, so adding or removing an address does not blank the
 * picker. A book that cannot be read at all falls back to the customer's list, which is what the picker offered
 * before there was a book.
 */
export function useAddressPicker(
  baskets: CustomerBasketAdapter,
  data: CustomerDataAdapter,
  customerCode: string | undefined,
  dependsOn: unknown,
): { state: AddressPickerState; reload: () => void } {
  const [state, setState] = useState<AddressPickerState>({ status: "loading" });
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const book = await baskets.getAddressBook().catch(() => null);
        const list =
          isBookThePicker(book) || !customerCode
            ? []
            : await data.listDeliveryAddresses(customerCode);
        if (!cancelled)
          setState({ status: "ready", picker: addressPickerFrom(book, list) });
      } catch (err) {
        if (!cancelled)
          setState({
            status: "error",
            message: err instanceof Error ? err.message : String(err),
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baskets, data, customerCode, dependsOn, version]);

  return { state, reload };
}
